import { SerializedConstructor } from '@langchain/core/load/serializable';
import { interpolateFString } from '@langchain/core/prompts';

import get from 'lodash-es/get';
import Mustache from 'mustache';
import { useMemo } from 'react';

import {
  convertSerializedMessagesToMessageTuple,
  interpolateFewShotPrompt,
} from '@/components/Rules/RulesEvaluatorPane.utils';
import { useExamples } from '@/hooks/useSwr';

import { useEvaluatorStore } from '../store/evaluatorStore';

export function stringifyAllValues(
  obj: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  // Iterate through all keys in the object
  Object.keys(obj).forEach((key) => {
    // Check if the value is an object (but not null)
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      // Stringify the object value
      result[key] = JSON.stringify(obj[key]);
    } else {
      // Keep primitive values as they are
      result[key] = obj[key];
    }
  });

  return result;
}

export function createVariableMapping(
  variableMapping: Record<string, string>,
  sources: {
    input: Record<string, unknown>;
    output?: Record<string, unknown>;
    referenceOutput?: Record<string, unknown>;
  }
): Record<string, unknown> {
  // Helper function to get nested values
  function getNestedValue(obj: Record<string, unknown>, path: string) {
    const value = get(obj, path);
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value);
    }
    return value;
  }

  return Object.entries(variableMapping).reduce((acc, [key, path]) => {
    if (!path) return acc;
    const [prefix, ...rest] = path.split('.');

    const sourceObj = sources[prefix as keyof typeof sources];
    if (rest.length === 0) {
      if (typeof sourceObj === 'object' && sourceObj !== null) {
        acc[key] = JSON.stringify(sourceObj);
      } else {
        acc[key] = sourceObj;
      }
    } else {
      acc[key] = sourceObj
        ? getNestedValue(sourceObj, rest.join('.'))
        : undefined;
    }

    return acc;
  }, {} as Record<string, unknown>);
}

export const useMessagePreviews = ({
  variableMapping,
  input,
  output,
  referenceOutput,
  manifest,
  fewShotDatasetId,
}: {
  variableMapping: Record<string, string>;
  input: Record<string, unknown>;
  output?: Record<string, unknown>;
  referenceOutput?: Record<string, unknown>;
  manifest: SerializedConstructor;
  fewShotDatasetId?: string;
}) => {
  const fewShotEnabled = useEvaluatorStore((state) => state.fewShotEnabled);
  const fewShotContent = useEvaluatorStore((state) => state.fewShotContent);
  const fewShotExamplesMap = useEvaluatorStore(
    (state) => state.fewShotExamplesMap
  );

  const correctionsDatasetExamples = useExamples(
    fewShotDatasetId
      ? {
          dataset: fewShotDatasetId,
          limit: 1,
        }
      : null
  );

  const correctionsDicts = useMemo(
    () =>
      correctionsDatasetExamples.data &&
      correctionsDatasetExamples.data?.length > 0
        ? [
            stringifyAllValues({
              ...correctionsDatasetExamples.data[0].outputs,
              ...correctionsDatasetExamples.data[0].inputs,
            }),
          ]
        : null,
    [correctionsDatasetExamples]
  );

  return useMemo(() => {
    // Create mapping with actual values from input/output/referenceOutput
    let mapping = createVariableMapping(variableMapping, {
      input,
      output,
      referenceOutput,
    });

    if (fewShotEnabled) {
      mapping = {
        ...mapping,
        examples_few_shot: correctionsDicts,
      };
    }

    const templateFormat =
      manifest.kwargs.first.kwargs.template_format ??
      manifest.kwargs.first.kwargs.messages?.[0]?.kwargs?.prompt?.kwargs
        ?.template_format;

    let prompts = convertSerializedMessagesToMessageTuple(
      manifest.kwargs.first.kwargs.messages
    );

    if (fewShotEnabled) {
      prompts = interpolateFewShotPrompt(
        prompts,
        fewShotContent,
        fewShotExamplesMap
      );
    }

    return prompts.map((prompt) => {
      if (templateFormat === 'mustache') {
        const content = Mustache.render(prompt[1], mapping);
        return [prompt[0], content];
      } else {
        try {
          const content = interpolateFString(prompt[1], mapping);
          return [prompt[0], content];
        } catch (e) {
          return [
            prompt[0],
            'Error formatting prompt - please ensure all template variables have a mapping.',
            true,
          ];
        }
      }
    }) as [string, string, boolean][];
  }, [
    variableMapping,
    fewShotEnabled,
    manifest.kwargs.first.kwargs.template_format,
    manifest.kwargs.first.kwargs.messages,
    input,
    output,
    referenceOutput,
    correctionsDicts,
    fewShotContent,
    fewShotExamplesMap,
  ]);
};
